import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { cn } from '@/lib/utils';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { trackEvent } from '@/utils/analytics';
import WhyRetailDataFailsSection from '@/components/WhyRetailDataFailsSection';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { retailProfitManagementCopy } from '@/contexts/language/translations/pages/retailProfitManagement';

/* ── Waterfall data ── */
const WATERFALL = [
  { id: 'ov', label: 'OV', fullName: 'Order Value', value: 100, deduction: null },
  { id: 'net-sales', label: 'Net\nSales', fullName: 'Net Sales', value: 82, deduction: { label: 'Returns', pct: 18 } },
  { id: 'gross-margin', label: 'Gross\nMargin', fullName: 'Gross Margin', value: 55, deduction: { label: 'COGS', pct: 27 } },
  { id: 'mktg-profit', label: 'MKTG\nProfit', fullName: 'Marketing Profit', value: 38, deduction: { label: 'Cost', pct: 17 } },
  { id: 'nc', label: 'NC', fullName: 'Net Contribution', value: 22, deduction: { label: 'VC', pct: 16 } },
  { id: 'ebit', label: 'EBIT', fullName: 'EBIT', value: 12, deduction: { label: 'OPEX', pct: 10 } },
];

/* Display copy (labels, metric explanations, provocations, calculator texts) lives in
   translations/pages/retailProfitManagement.ts. WATERFALL keeps English fullName for analytics. */

/* ── DIY Profit Calculator ── */

// Labels, placeholders and tooltips per key live in the copy file (calc.fields).
const INPUT_FIELDS = [
  { key: 'returnRate' },
  { key: 'grossMargin' },
  { key: 'mir' },
  { key: 'paymentCost' },
  { key: 'deliveryCost' },
  { key: 'opex' },
] as const;

type CalcInputs = Record<(typeof INPUT_FIELDS)[number]['key'], string>;

const EMPTY_INPUTS: CalcInputs = {
  returnRate: '', grossMargin: '', mir: '', paymentCost: '', deliveryCost: '', opex: '',
};

interface CalcStep {
  label: string;
  fullLabel: string;
  value: number;
  deduction: { label: string; pct: number } | null;
  isEbit?: boolean;
}

function computeWaterfall(inputs: CalcInputs): CalcStep[] | null {
  const vals = Object.values(inputs);
  if (vals.some(v => v === '' || isNaN(Number(v)))) return null;

  const ret = Number(inputs.returnRate);
  const gm = Number(inputs.grossMargin);
  const mir = Number(inputs.mir);
  const pay = Number(inputs.paymentCost);
  const del = Number(inputs.deliveryCost);
  const opex = Number(inputs.opex);

  // Net Sales = 100% (base). OV = 100 / (1 - returnRate).
  // All other inputs map directly to visible % on the chart.
  const netSales = 100;
  const ov = +(netSales / (1 - ret / 100)).toFixed(1);
  const returnsVal = +(ov - netSales).toFixed(1);
  const grossMarginVal = netSales * (gm / 100);
  const cogsVal = netSales - grossMarginVal;
  const mktgCost = netSales * (mir / 100);
  const mktgProfit = grossMarginVal - mktgCost;
  const vc = (pay / 100 + del / 100) * netSales;
  const nc = mktgProfit - vc;
  const opexVal = opex / 100 * netSales;
  const ebit = nc - opexVal;

  return [
    { label: 'OV', fullLabel: 'Order Value', value: +ov.toFixed(1), deduction: null },
    { label: 'Net\nSales', fullLabel: 'Net Sales', value: +netSales.toFixed(1), deduction: { label: 'Returns', pct: returnsVal } },
    { label: 'Gross\nMargin', fullLabel: 'Gross Margin', value: +grossMarginVal.toFixed(1), deduction: { label: 'COGS', pct: +cogsVal.toFixed(1) } },
    { label: 'MKTG\nProfit', fullLabel: 'Marketing Profit', value: +mktgProfit.toFixed(1), deduction: { label: 'MKTG', pct: +mktgCost.toFixed(1) } },
    { label: 'NC', fullLabel: 'Net Contribution', value: +nc.toFixed(1), deduction: { label: 'VC', pct: +vc.toFixed(1) } },
    { label: 'EBIT', fullLabel: 'EBIT', value: +ebit.toFixed(1), deduction: { label: 'OPEX', pct: +opexVal.toFixed(1) }, isEbit: true },
  ];
}

const ProfitCalculator = () => {
  const { language } = useLanguage();
  const c = retailProfitManagementCopy[language];
  const [inputs, setInputs] = useState<CalcInputs>(EMPTY_INPUTS);
  const [result, setResult] = useState<CalcStep[] | null>(null);

  const handleChange = (key: string, value: string) => {
    // Slovak visitors type a decimal comma — store it as a dot so the calculation stays identical.
    const normalized = language === 'sk' ? value.replace(/,/g, '.') : value;
    const cleaned = normalized.replace(/[^0-9.]/g, '');
    setInputs(prev => ({ ...prev, [key]: cleaned }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const waterfall = computeWaterfall(inputs);
    setResult(waterfall);
    if (waterfall) {
      const ebitValue = waterfall[waterfall.length - 1].value;
      trackEvent('calculator_completed', 'engagement', 'profit_calculator', undefined);
      // Fire with all input params as a single structured event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'calculator_completed', {
          return_rate_pct: Number(inputs.returnRate),
          gross_margin_pct: Number(inputs.grossMargin),
          mir_pct: Number(inputs.mir),
          payment_cost_pct: Number(inputs.paymentCost),
          delivery_cost_pct: Number(inputs.deliveryCost),
          opex_pct: Number(inputs.opex),
          ebit_result_pct: ebitValue,
          ebit_positive: ebitValue >= 0,
        });
      }
    }
  };

  const handleReset = () => {
    setInputs(EMPTY_INPUTS);
    setResult(null);
  };

  const maxH = 320;

  return (
    <section id="calculator" className="py-20 md:py-28 bg-[#FAFAFA]">
      <div className="container-custom">
        <div className="text-center mb-12 reveal">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.calc.eyebrow}</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-3">
            {c.calc.title}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {c.calc.subtitle}
          </p>
        </div>

        {/* Input form */}
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto mb-16">
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 md:p-8">
            <p className="text-sm font-semibold text-[#2A1E47]/50 uppercase tracking-wider mb-6">{c.calc.allValues}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {INPUT_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="flex items-start gap-1.5 text-sm font-medium text-[#2A1E47] mb-1.5 min-h-[2.5rem]">
                    {c.calc.fields[field.key].label}
                    <span className="relative group cursor-help">
                      <svg className="w-3.5 h-3.5 text-[#2A1E47]/30 hover:text-[#FF6B35] transition-colors" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#2A1E47] text-white text-xs leading-snug w-52 text-center opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-lg">
                        {c.calc.fields[field.key].tooltip}
                      </span>
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={language === 'sk' ? inputs[field.key].replace('.', ',') : inputs[field.key]}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                      placeholder={c.calc.fields[field.key].placeholder}
                      className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 pr-8 text-[#2A1E47] text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-colors"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#2A1E47]/30 font-medium">%</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button
                type="submit"
                className="px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
              >
                {c.calc.calculate}
              </button>
              {result && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-6 py-3 rounded-lg border border-gray-200 text-[#2A1E47]/60 font-medium hover:bg-gray-50 transition-colors"
                >
                  {c.calc.reset}
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Result waterfall */}
        {result && (
          <div className="max-w-5xl mx-auto animate-fade-in-up">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-[#2A1E47] mb-2">{c.calc.resultTitle}</h3>
              {(() => {
                const ebit = result[result.length - 1];
                return ebit.value >= 0 ? (
                  <p className="text-emerald-600 font-medium">{c.calc.ebitPositive(c.pct(ebit.value))}</p>
                ) : (
                  <p className="text-red-500 font-medium">{c.calc.ebitNegative(c.pct(ebit.value))}</p>
                );
              })()}
            </div>

            <div className="px-4">
              <div className="flex items-end justify-center gap-1 sm:gap-2 md:gap-3">
                {result.map((step, stepIndex) => {
                  const stepCopy = c.calc.steps[stepIndex];
                  const ovVal = result[0].value; // OV is the tallest bar, use as scale base
                  const clampedVal = Math.max(step.value, 0);
                  const barH = Math.max((clampedVal / ovVal) * maxH, 4);
                  const deductH = step.deduction ? Math.max((step.deduction.pct / ovVal) * maxH, 0) : 0;
                  const ebit = result[result.length - 1];
                  const isNegativeEbit = step.isEbit && ebit.value < 0;

                  return (
                    <div key={step.label} className="flex flex-col items-center">
                      {/* Negative EBIT indicator */}
                      {isNegativeEbit && (
                        <div
                          className="w-16 sm:w-20 md:w-28 lg:w-32 flex items-center justify-center rounded-t-lg mb-px"
                          style={{
                            height: Math.max((Math.abs(ebit.value) / 100) * maxH, 24),
                            backgroundColor: '#FEE2E2',
                            border: '2px dashed #EF4444',
                          }}
                        >
                          <span className="text-xs font-bold text-red-500">{c.pct(ebit.value)}</span>
                        </div>
                      )}
                      {/* Deduction block */}
                      {step.deduction && deductH > 0 && (
                        <div
                          className="w-16 sm:w-20 md:w-28 lg:w-32 flex items-center justify-center border border-white/50"
                          style={{ height: deductH, backgroundColor: '#FDDCB5' }}
                        >
                          <span className="text-xs sm:text-sm font-bold text-[#2A1E47]/80 text-center leading-tight px-1">
                            {stepCopy.deduction}
                            <span className="block text-xs font-normal text-[#2A1E47]/60">{c.pct(step.deduction.pct)}</span>
                          </span>
                        </div>
                      )}
                      {/* Value block */}
                      <div
                        className="w-16 sm:w-20 md:w-28 lg:w-32 flex items-center justify-center border border-white/50"
                        style={{
                          height: barH,
                          backgroundColor: step.isEbit
                            ? ebit.value >= 0 ? '#B8F0D1' : '#FEE2E2'
                            : '#FFF8E7',
                        }}
                      >
                        <span className={cn(
                          'text-sm sm:text-base md:text-xl font-bold text-center leading-tight whitespace-pre-line px-1',
                          step.isEbit && ebit.value < 0 ? 'text-red-600' : 'text-[#2A1E47]'
                        )}>
                          {stepCopy.label}
                        </span>
                      </div>
                      {/* Label */}
                      <div className="mt-3 flex flex-col items-center">
                        <span className={cn(
                          'text-sm font-bold',
                          step.isEbit && ebit.value < 0 ? 'text-red-500' : step.isEbit ? 'text-emerald-600' : 'text-[#2A1E47]/60'
                        )}>
                          {c.pct(step.value)}
                        </span>
                        <span className="hidden md:block text-xs text-muted-foreground mt-0.5">{stepCopy.fullLabel}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="max-w-3xl mx-auto mt-6">
                <div className="h-px bg-[#2A1E47]/10" />
              </div>
            </div>

            {/* Post-result CTA */}
            <div className="mt-12 text-center">
              <p className="text-[#2A1E47]/60 text-sm mb-4">{c.calc.ctaQuestion}</p>
              <Link
                to="/tell-me-more"
                onClick={() => trackEvent('profitability_improvement_request', 'conversion', 'calculator_cta_click')}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#2A1E47] text-white font-semibold text-base hover:bg-[#2A1E47]/90 transition-colors shadow-lg"
              >
                {c.calc.ctaButton}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const OurWorkRetailManagement = () => {
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const { language } = useLanguage();
  const c = retailProfitManagementCopy[language];
  useScrollReveal();

  const maxH = 380;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seoTitle}
        description={c.seoDescription}
        canonical="/our-work/retail-profit-management"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-60" />
          <div className="absolute bottom-20 right-0 w-[400px] h-[400px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-40" />

          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                Retail Profit<br />Management
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-4">
                {c.hero.tagline}
              </p>
              <p className="text-base text-muted-foreground/70 max-w-xl mx-auto mb-10">
                {c.hero.subtitle}
              </p>
              <div className={`flex flex-col sm:flex-row items-center justify-center gap-4${c.hero.ctaNote ? ' sm:items-stretch' : ''}`}>
                <Link
                  to="/tell-me-more"
                  className={`inline-flex items-center justify-center rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors ${c.hero.ctaNote ? 'flex-col py-3 px-6 sm:px-8' : 'px-8 py-3'}`}
                >
                  {c.hero.ctaNote ? (
                    <>
                      <span className="text-lg sm:text-xl whitespace-nowrap">{c.hero.ctaPrimary}</span>
                      <span className="text-[10px] sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{c.hero.ctaNote}</span>
                    </>
                  ) : c.hero.ctaPrimary}
                </Link>
                <button
                  onClick={() => {
                    trackEvent('profit_calculator_top_button', 'engagement', 'hero_cta_click');
                    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-lg border-2 border-[#2A1E47]/20 text-[#2A1E47] font-medium hover:border-[#2A1E47]/40 hover:bg-[#2A1E47]/5 transition-colors"
                >
                  {c.hero.ctaCalculator}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Provocation section */}
        <section className="py-16 md:py-24 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="max-w-3xl mx-auto">
              {c.provocations.map((p, i) => (
                <div key={i} className="mb-8 last:mb-0">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{p.q}</h3>
                  {p.a && <p className="text-lg text-white/60">{p.a}</p>}
                </div>
              ))}

              <div className="mt-10 pl-6 border-l-2 border-[#FF6B35]">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.correctWayLabel}</p>
                {c.correctWay.map((line, i) => (
                  <p key={i} className="text-white text-lg mb-2 last:mb-0">
                    <span className="text-[#FF6B35] font-bold mr-2">{i + 1}.</span>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Waterfall */}
        <section className="py-20 md:py-28 bg-white overflow-hidden">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-3">
              {c.waterfall.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-4">
              {c.waterfall.subtitle}
            </p>
            <p className="text-center text-sm text-muted-foreground/60 mb-16">
              {c.waterfall.note}
            </p>

            {/* Waterfall chart */}
            <div className="max-w-5xl mx-auto px-4">

              {/* Annotations row — sits above the bars */}
              <div className="hidden md:flex items-end justify-center gap-1 sm:gap-2 md:gap-3 mb-0">
                {WATERFALL.map((step) => (
                  <div key={step.id} className="w-16 sm:w-20 md:w-28 lg:w-32 flex flex-col items-center">
                    {step.id === 'ov' && (
                      <div className="flex flex-col items-center mb-2">
                        <div className="bg-[#FF6B35] rounded-xl px-3 py-2 mb-2 shadow-md">
                          <span className="text-xs text-white font-semibold text-center leading-snug block max-w-[110px]">
                            {c.waterfall.annotationOv}
                          </span>
                        </div>
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="none">
                          <path d="M12 0 L12 12" stroke="#FF6B35" strokeWidth="2" strokeDasharray="3 2"/>
                          <path d="M6 10 L12 18 L18 10" stroke="#FF6B35" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                        </svg>
                      </div>
                    )}
                    {step.id === 'nc' && (
                      <div className="flex flex-col items-center mb-2">
                        <div className="bg-[#2A1E47] rounded-xl px-3 py-2 mb-2 shadow-md">
                          <span className="text-xs text-white font-semibold text-center leading-snug block max-w-[110px]">
                            {c.waterfall.annotationNc}
                          </span>
                        </div>
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="none">
                          <path d="M12 0 L12 12" stroke="#2A1E47" strokeWidth="2" strokeDasharray="3 2"/>
                          <path d="M6 10 L12 18 L18 10" stroke="#2A1E47" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-end justify-center gap-1 sm:gap-2 md:gap-3">
                {WATERFALL.map((step) => {
                  const barH = (step.value / 100) * maxH;
                  const deductH = step.deduction ? (step.deduction.pct / 100) * maxH : 0;
                  const isActive = activeMetric === step.id;
                  const isEbit = step.id === 'ebit';

                  return (
                    <button
                      key={step.id}
                      onClick={() => setActiveMetric(isActive ? null : step.id)}
                      className={cn(
                        'group flex flex-col items-center transition-all duration-300 cursor-pointer',
                        isActive ? 'scale-[1.04]' : 'hover:scale-[1.02]'
                      )}
                    >
                      {/* Deduction block sits on top of the value block */}
                      <div className="flex flex-col items-center">
                        {step.deduction && (
                          <div
                            className={cn(
                              'w-16 sm:w-20 md:w-28 lg:w-32 flex items-center justify-center border border-white/50 transition-all duration-300',
                              isActive ? 'ring-2 ring-[#FF6B35]/40' : ''
                            )}
                            style={{
                              height: deductH,
                              backgroundColor: '#FDDCB5',
                            }}
                          >
                            <span className="text-xs sm:text-sm md:text-base font-bold text-[#2A1E47]/80 text-center leading-tight px-1">
                              {c.waterfall.steps[step.id as keyof typeof c.waterfall.steps].deduction}
                            </span>
                          </div>
                        )}
                        <div
                          className={cn(
                            'w-16 sm:w-20 md:w-28 lg:w-32 flex items-center justify-center border border-white/50 transition-all duration-300',
                            isActive ? 'ring-2 ring-[#FF6B35]/40' : ''
                          )}
                          style={{
                            height: barH,
                            backgroundColor: isEbit
                              ? '#B8F0D1'
                              : isActive
                                ? '#FFF0D6'
                                : '#FFF8E7',
                          }}
                        >
                          {(() => {
                            const label = c.waterfall.steps[step.id as keyof typeof c.waterfall.steps].label;
                            // Long single words (e.g. sk "objednávok") get one size smaller so they fit the bar
                            const hasLongWord = label.split(/\s/).some((w) => w.length > 8);
                            return (
                              <span className={`${hasLongWord ? 'text-[10px] sm:text-sm md:text-lg lg:text-xl' : 'text-sm sm:text-base md:text-xl lg:text-2xl'} font-bold text-[#2A1E47] text-center leading-tight whitespace-pre-line px-1`}>
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Spacer below bar */}
                      <div className="mt-3" />
                    </button>
                  );
                })}
              </div>

              {/* Baseline */}
              <div className="max-w-3xl mx-auto mt-6">
                <div className="h-px bg-[#2A1E47]/10" />
              </div>
            </div>

            {/* Detail card */}
            {activeMetric && c.metricDetails[activeMetric as keyof typeof c.metricDetails] && (
              <div className="mt-12 max-w-3xl mx-auto animate-fade-in-up">
                {(() => {
                  const step = WATERFALL.find(s => s.id === activeMetric)!;
                  const detail = c.metricDetails[activeMetric as keyof typeof c.metricDetails];
                  const stepCopy = c.waterfall.steps[step.id as keyof typeof c.waterfall.steps];
                  return (
                    <div className="rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
                      <div className="px-8 py-5 bg-[#2A1E47] flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-white">{stepCopy.fullName}</h3>
                          <p className="text-white/50 text-sm">{c.detail.remains(step.value)}</p>
                        </div>
                        {step.deduction && (
                          <div className="text-right">
                            <span className="text-sm text-[#FDDCB5] font-medium">−{c.pct(step.deduction.pct)}</span>
                            <p className="text-xs text-white/40">{stepCopy.deduction}</p>
                          </div>
                        )}
                      </div>
                      <div className="px-8 py-6 bg-white space-y-4">
                        <div>
                          <p className="text-sm font-semibold text-[#FF6B35] uppercase tracking-wider mb-1">{c.detail.question}</p>
                          <p className="text-[#2A1E47] font-medium">{detail.question}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#FF6B35] uppercase tracking-wider mb-1">{c.detail.insight}</p>
                          <p className="text-muted-foreground">{detail.insight}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#FF6B35] uppercase tracking-wider mb-1">{c.detail.action}</p>
                          <p className="text-muted-foreground">{detail.action}</p>
                        </div>
                        <Link
                          to="/tell-me-more"
                          onClick={() => trackEvent('tell_me_more_click', 'engagement', step.fullName)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#FF6B35]/30 text-[#FF6B35] text-sm font-semibold hover:bg-[#FF6B35]/5 transition-colors"
                        >
                          {c.detail.cta}
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </section>

        {/* What most retailers get wrong – shared content, see WhyRetailDataFailsSection.tsx */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <WhyRetailDataFailsSection variant="numbered" trackingLabel="why_retail_data_fails_section" />
          </div>
        </section>

        {/* How we do it */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.approach.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-12">
                {c.approach.title}
              </h2>

              <div className="space-y-0">
                {c.approach.phases.map((phase, i) => (
                  <div key={phase.phase} className="relative pl-12 md:pl-16 pb-12 last:pb-0">
                    {i < 2 && (
                      <div className="absolute left-[19px] md:left-[23px] top-10 bottom-0 w-px bg-[#2A1E47]/10" />
                    )}
                    <div
                      className="absolute left-0 top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: i === 2 ? '#FF6B35' : '#2A1E47' }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#2A1E47]">{phase.title}</h3>
                        <span className="text-xs font-medium text-[#FF6B35] bg-[#FFF7EC] px-2 py-0.5 rounded-full">{phase.duration}</span>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-[#261941]">
                <div className="container-custom py-10 md:py-14 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold leading-tight">
                      <span className="text-white">{c.approach.digitalCfo.titleStart}</span>
                      <span className="text-[#FF6B35]">{c.approach.digitalCfo.titleAccent}</span>
                    </h3>
                    <p className="text-base md:text-lg text-white/70 mt-3">
                      {c.approach.digitalCfo.textBefore}<span className="text-white font-semibold">{c.approach.digitalCfo.textBold}</span>{c.approach.digitalCfo.textAfter}
                    </p>
                  </div>
                  <Link
                    to="/our-work/digital-cfo"
                    onClick={() => trackEvent('rpm_to_digital_cfo', 'navigation', 'cross_link_click')}
                    className="inline-flex items-center justify-center gap-2 flex-shrink-0 px-6 py-3 rounded-lg bg-[#FF6B35] text-white font-semibold hover:bg-[#FF6B35]/90 transition-colors"
                  >
                    {c.approach.digitalCfo.button}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DIY Profit Calculator */}
        <ProfitCalculator />

        {/* CTA banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.finalCta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.finalCta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.finalCta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default OurWorkRetailManagement;
